import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
} from 'firebase/auth';
import { getAuthDeferred } from '@/utils/firebase';
import { GoogleAuthProvider } from 'firebase/auth';
import { useAuth } from '@/lib/auth';
import { client, createUserWithCompany, createUserWithCompanyMutation, acceptInvitation, acceptInvitationMutation, AcceptInvitationRequest } from '@/api';
import { toast } from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

type FieldValues = {
  email: string;
  password: string;
};

type CompanyCreationInfo = {
  name: string;
  account_type: "company_admin";
}

type CreateAccountDataCompany = CompanyCreationInfo & {
  email: string;
  first_name: string;
  last_name: string;
  profile_image: string;
  uid?: string;
  provider: string;
}


function useUser() {
  const { user, isInitialLoading, logout, setUser, getUser } = useAuth();
  const navigate = useNavigate();

  const createAccountMutation = useMutation({
    ...createUserWithCompanyMutation(),
    onSuccess: async (data) => {
      setUser(data);
      navigate({ to: '/dashboard' });
    },
    onError: (error: any) => {
      console.error('Failed to create company account with Google:', error);
      toast.error('Failed to create company account. Please try again.', {
        duration: 1000,
      });
    },
  });

  function errorHandler(error: any) {
    console.error('Failed to create company account:', error);
    // FIREBASE ERROR CONTAINS CODES; API errors carry response.data.detail
    const detail = error?.response?.data?.detail;
    if (error?.code === 'auth/email-already-in-use' || (typeof detail === 'string' && detail.includes('EMAIL_EXISTS'))) {
      toast.error('Email already in use. Please try with a different email or login.');
    } else if (error?.code === 'auth/weak-password') {
      toast.error('Password is too weak. Please choose a stronger password.');
    } else if (error?.code === 'auth/invalid-email') {
      toast.error('Invalid email address. Please check and try again.');
    } else {
      toast.error('Failed to create account. Please try again.');
    }
  }

  // Create Company Account Mutation
  const createCompanyAccountMutation = useMutation({
    mutationFn: async ({
      email,
      password,
      data,
    }: {
      email: string;
      password: string;
      data: CreateAccountDataCompany;
    }) => {
      const auth = getAuthDeferred();
      // Create company and user in API
      const response = await createUserWithCompany({
        body: {
          // Note here that when non google signup we don't have uid yet, we first create account on the api and then we update the uid
          ...data,
          uid: data.uid || '',
          password: password,
        },
        throwOnError: true,

      });
      await signInWithEmailAndPassword(auth, email, password);

      return { email, password, data, template_id: response.data.template_id };
    },
    onSuccess: async ({ email, password, data, template_id }) => {
   
      navigate({ to: '/verify-email' });
    },
    onError: errorHandler
  });

  const acceptMemberInvitation = useMutation({
    mutationFn: async (variables: AcceptInvitationRequest) => {
      await acceptInvitation({
        body: variables,
        throwOnError: true,
      });
      const auth = getAuthDeferred();
      if (variables.user_details.password) {
        await signInWithEmailAndPassword(auth, variables.user_details.email, variables.user_details.password);
      } else if (auth.currentUser) {
        // Google users are already signed in, so onAuthStateChanged won't fire again; load the new member record ourselves
        const member = await getUser(await auth.currentUser.getIdToken());
        if (member) setUser(member);
      }

      return variables;
    },
    onError: (error: any) => {
      console.error('Failed to accept invitation:', error);
      if (error?.response?.data?.detail === 'invitation-invalid-email-nomatch') {
        toast.error('Use the email this invite was sent to.');
        return;
      }
      toast.error('Failed to accept invitation. Please try again.');
    },
  });

  // Login Mutation
  const loginMutation = useMutation({
    mutationFn: async ({ email, password }: FieldValues) => {
      const auth = getAuthDeferred();
      const result = await signInWithEmailAndPassword(auth, email, password);
      return { result };
    },
    onError: errorHandler
  });

  // Google Login Mutation
  const loginGoogleMutation = useMutation({
    mutationFn: async () => {
      const auth = getAuthDeferred();
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      return { result };
    },
    onError: (error: any) => {
      // Closing the Google window is a choice, not an error
      if (error?.code === 'auth/popup-closed-by-user' || error?.code === 'auth/cancelled-popup-request') return;
      console.error('Google sign-in failed:', error);
      if (error?.code === 'auth/popup-blocked') {
        toast.error('Allow pop-ups for this site to continue with Google.');
      } else if (error?.code === 'auth/network-request-failed') {
        toast.error('No connection. Try again.');
      } else {
        toast.error("Google sign-in didn't work. Try again or use email.");
      }
    },
  });

  // Logout Mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      logout();
    },
    onSuccess: () => {
      window.location.href = '/';

    },
    onError: (error: any) => {
      console.error('Logout failed:', error);
      toast.error('Failed to sign out. Please try again.');
    },
  });

  return {
    user: user,

    // Mutation objects
    createCompanyAccountMutation,
    createCompanyAccountWithGoogleMutation: createAccountMutation,
    acceptMemberInvitation,
    login: loginMutation.mutate,
    loginGoogle: loginGoogleMutation.mutate,
    logout: logoutMutation.mutate,

    // States
    isAuthenticating:
      isInitialLoading ||
      loginMutation.isPending ||
      loginGoogleMutation.isPending,

  };
}

export default useUser;
